import type { ProductionOrder, ProductionOrderStatus } from '@erp/shared';
import { isAxiosError } from 'axios';
import { ArrowLeft, Factory, Hammer, Pencil, Undo2, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { Button, confirmDialog } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { formatDateTime } from '../../lib/intl.js';
import { formatMoney, trimMoney } from '../../lib/money.js';

const STATUS_TONE: Record<ProductionOrderStatus, 'success' | 'secondary' | 'danger' | 'default'> = {
  posted: 'success',
  draft: 'secondary',
  voided: 'danger',
  cancelled: 'default',
};

/**
 * One production order: what it makes (or unmakes), from what, and — once
 * posted — what every component actually cost, frozen at post time. The owner's
 * "what did it cost me to make this" answer lives in this table.
 */
export function ProductionOrderDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation('manufacturing');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const canWrite = usePermission('manufacturing.write');
  const canPost = usePermission('manufacturing.post');

  const [order, setOrder] = useState<ProductionOrder | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async (): Promise<void> => {
    if (!id) return;
    try {
      const r = await api().get<ProductionOrder>(`/manufacturing/orders/${id}`);
      setOrder(r.data);
    } catch {
      toast.error(t('orders.err.loadFailed', { defaultValue: 'Could not load the order.' }));
      navigate('/manufacturing/orders', { replace: true });
    }
  }, [id, navigate, t]);

  useEffect(() => { void reload(); }, [reload]);

  if (!order) return <Card className="p-6"><Skeleton className="h-40 w-full" /></Card>;

  const assembling = order.kind === 'assemble';

  const failToast = (err: unknown): void => {
    const cause = isAxiosError(err)
      ? (err.response?.data as { cause?: { code?: string; itemName?: string; available?: string; requested?: string; required?: string; from?: string; to?: string } } | undefined)?.cause
      : undefined;
    if (cause?.code === 'stock.insufficient') {
      toast.error(t('orders.err.insufficient', {
        item: cause.itemName ?? '—', available: trimMoney(cause.available ?? '0'), requested: trimMoney(cause.requested ?? '0'),
        defaultValue: 'Not enough "{{item}}" on hand — {{available}} available, {{requested}} needed.',
      }));
    } else if (cause?.code === 'production_unwind_negative') {
      toast.error(t('orders.err.unwindNegative', {
        item: cause.itemName ?? '—', required: trimMoney(cause.required ?? '0'), available: trimMoney(cause.available ?? '0'),
        defaultValue: 'Cannot void — "{{item}}" was already sold or used ({{available}} left of the {{required}} this order made).',
      }));
    } else if (cause?.code === 'production_cost_no_rate') {
      toast.error(t('orders.err.noRate', {
        from: cause.from ?? '—', to: cause.to ?? '—',
        defaultValue: 'No exchange rate between {{from}} and {{to}} — add one in Settings → Currencies.',
      }));
    } else if (cause?.code === 'production_cost_currency_required') {
      toast.error(t('orders.err.currencyRequired', { defaultValue: 'Components are costed in different currencies — set the cost currency on the order first.' }));
    } else if (cause?.code?.startsWith('invalid_status')) {
      toast.error(t('orders.err.invalidStatus', { defaultValue: 'The order changed state — reload the page.' }));
    } else {
      toast.error(t('orders.err.postFailed', { defaultValue: 'That did not go through.' }));
    }
  };

  const act = async (verb: 'post' | 'void' | 'cancel'): Promise<void> => {
    const confirms = {
      post: {
        title: assembling ? t('orders.postTitle', { defaultValue: 'Make it now?' }) : t('orders.postTitleDisassemble', { defaultValue: 'Take it apart now?' }),
        message: assembling
          ? t('orders.postConfirm', {
              n: order.lines.length, qty: trimMoney(order.quantity), name: order.itemName,
              defaultValue: 'Consumes {{n}} component line(s) and adds {{qty}} × {{name}} to stock at the computed cost.',
            })
          : t('orders.postConfirmDisassemble', {
              qty: trimMoney(order.quantity), name: order.itemName,
              defaultValue: 'Consumes {{qty}} × {{name}} and returns its components to stock.',
            }),
        tone: 'default' as const,
        label: t('orders.post', { defaultValue: 'Post' }),
      },
      void: {
        title: t('orders.voidTitle', { defaultValue: 'Void this order?' }),
        message: t('orders.voidConfirm', { doc: order.docNumber, defaultValue: 'Every stock movement of {{doc}} is reversed — components come back at the cost they left at.' }),
        tone: 'danger' as const,
        label: t('orders.void', { defaultValue: 'Void' }),
      },
      cancel: {
        title: t('orders.cancelTitle', { defaultValue: 'Cancel this draft?' }),
        message: t('orders.cancelConfirm', { defaultValue: 'The draft is kept for the record but can no longer be posted.' }),
        tone: 'danger' as const,
        label: t('orders.cancelIt', { defaultValue: 'Cancel draft' }),
      },
    }[verb];
    const ok = await confirmDialog({ title: confirms.title, message: confirms.message, tone: confirms.tone, confirmLabel: confirms.label });
    if (!ok) return;
    setBusy(true);
    try {
      await api().post(`/manufacturing/orders/${order.id}/${verb}`, {});
      toast.success(t(`orders.done.${verb}`, {
        doc: order.docNumber,
        defaultValue: { post: '{{doc}} posted — stock updated.', void: '{{doc}} voided — stock restored.', cancel: 'Draft cancelled.' }[verb],
      }));
      await reload();
    } catch (err) {
      failToast(err);
    } finally {
      setBusy(false);
    }
  };

  const money = (v: string | null, ccy: string | null): string =>
    (v !== null && ccy ? formatMoney(v, { locale: i18n.language, currency: ccy }) : '—');

  return (
    <div className="flex flex-col gap-4">
      <Link to="/manufacturing/orders" className="inline-flex w-fit items-center gap-1 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft className="h-3.5 w-3.5 rtl:rotate-180" aria-hidden />
        {t('orders.title', { defaultValue: 'Production orders' })}
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            {assembling ? <Hammer className="h-6 w-6 text-brand" aria-hidden /> : <Undo2 className="h-6 w-6 text-brand" aria-hidden />}
            {order.docNumber}
            <Badge variant={STATUS_TONE[order.status]}>{t(`orders.state.${order.status}`, { defaultValue: order.status })}</Badge>
          </h1>
          <p className="mt-1 text-sm text-fg-muted">
            {t(`orders.kinds.${order.kind}`, { defaultValue: order.kind })} · {trimMoney(order.quantity)}{order.unitName ? ` ${order.unitName}` : ''} × <span className="font-medium text-fg">{order.itemName}</span> · {order.warehouseName}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {order.status === 'draft' && canWrite && (
            <>
              <Button variant="secondary" disabled={busy} onClick={() => navigate(`/manufacturing/orders/${order.id}/edit`)}>
                <Pencil className="me-1.5 h-4 w-4" aria-hidden />
                {t('orders.edit', { defaultValue: 'Edit' })}
              </Button>
              <Button variant="secondary" disabled={busy} onClick={() => void act('cancel')}>
                <XCircle className="me-1.5 h-4 w-4" aria-hidden />
                {t('orders.cancelIt', { defaultValue: 'Cancel draft' })}
              </Button>
            </>
          )}
          {order.status === 'draft' && canPost && (
            <Button disabled={busy} loading={busy} onClick={() => void act('post')}>
              <Factory className="me-1.5 h-4 w-4" aria-hidden />
              {t('orders.post', { defaultValue: 'Post' })}
            </Button>
          )}
          {order.status === 'posted' && canPost && (
            <Button variant="secondary" disabled={busy} loading={busy} onClick={() => void act('void')}>
              <Undo2 className="me-1.5 h-4 w-4" aria-hidden />
              {t('orders.void', { defaultValue: 'Void' })}
            </Button>
          )}
        </div>
      </div>

      {order.status === 'posted' && (
        <Card className="flex flex-wrap items-center gap-x-8 gap-y-2 p-4 text-sm">
          <span>
            <span className="text-fg-muted">{t('orders.totalCost', { defaultValue: 'Total cost' })}: </span>
            <span className="font-mono font-semibold">{money(order.totalComponentCost, order.costCurrencyCode)}</span>
            {assembling && Number(order.extraCost) > 0 && (
              <span className="text-fg-muted"> ({t('orders.inclExtra', { extra: money(order.extraCost, order.extraCostCurrencyCode ?? order.costCurrencyCode), defaultValue: 'incl. {{extra}} extra' })})</span>
            )}
          </span>
          <span>
            <span className="text-fg-muted">{t('orders.unitCost', { defaultValue: 'Unit cost' })}: </span>
            <span className="font-mono font-semibold">{money(order.unitCost, order.costCurrencyCode)}</span>
          </span>
          {order.postedAt && (
            <span className="text-fg-muted">{t('orders.postedAt', { defaultValue: 'Posted' })}: {formatDateTime(order.postedAt, { locale: i18n.language })}</span>
          )}
        </Card>
      )}

      <Card className="overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead className="bg-bg-muted text-fg-subtle">
            <tr>
              <th className="w-10 px-3 py-2 text-start font-medium">#</th>
              <th className="px-3 py-2 text-start font-medium">
                {assembling ? t('orders.form.component', { defaultValue: 'Component (consumed)' }) : t('orders.form.componentBack', { defaultValue: 'Component (returned)' })}
              </th>
              <th className="w-28 px-3 py-2 text-end font-medium">{t('orders.form.total', { defaultValue: 'Qty' })}</th>
              <th className="w-36 px-3 py-2 text-end font-medium">{t('orders.frozenAvg', { defaultValue: 'Cost at post' })}</th>
              <th className="w-36 px-3 py-2 text-end font-medium">{t('orders.inOrderCcy', { defaultValue: 'In order currency' })}</th>
            </tr>
          </thead>
          <tbody>
            {order.lines.map((l, idx) => (
              <tr key={l.id} className="border-t border-border">
                <td className="px-3 py-2 text-fg-muted">{idx + 1}</td>
                <td className="px-3 py-2">
                  <div className="font-medium">{l.itemName}</div>
                  <div className="font-mono text-xs text-fg-muted">{l.itemCode} · {l.itemUom}</div>
                </td>
                <td className="px-3 py-2 text-end font-mono">{trimMoney(l.quantity)}{l.unitName ? ` ${l.unitName}` : ''}</td>
                <td className="px-3 py-2 text-end font-mono text-fg-muted">{money(l.avgCost, l.avgCostCurrencyCode)}</td>
                <td className="px-3 py-2 text-end font-mono">{money(l.costInOrderCurrency, order.costCurrencyCode)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {order.notes && (
        <Card className="p-4 text-sm">
          <span className="font-semibold">{t('orders.notes', { defaultValue: 'Notes' })}: </span>
          <span className="whitespace-pre-wrap">{order.notes}</span>
        </Card>
      )}
    </div>
  );
}
